import type { FormInputErrors, InputErrorMessage, ListItem, ListItemState } from "./types";

export function getDefaultBaseInputProps() {
  const { labelText, ...result } = BaseZodFormInputPropsDefaults;
  return result;
}

export function getDefaultInputProps() {
  return {
    ...getDefaultBaseInputProps(),
    wrapperClasses: undefined,
    wrapperDisabledClasses: undefined,
    wrapperErrorClasses: undefined,
    labelText: undefined,
    labelClasses: undefined,
  };
}

/**
 * When on DB the date is saved in the format 2025-10-03T06:00:00 (removing the ISO sufix)
 * e.g 2025-10-03T06:00:00.000Z to 2025-10-03T06:00:00
 * @param value
 * @returns
 */
export function normalizeDateStrForDB(value: string | Date | null | undefined) {
  if (!value) return null;

  const date = new Date(value);
  if (isNaN(date.getTime())) return null;

  // Taken from src/clients/ckclient/app/components/input/DatePicker.vue:57 (formattedDate)
  return `${date.getUTCFullYear()}-${(date.getUTCMonth() + 1).toString().padStart(2, "0")}-${date.getUTCDate().toString().padStart(2, "0")}T00:00:00`;
}

export function serializeInputValue(value: InputValue) {
  if (value === undefined || value === null) return "";
  if (value instanceof Date && !isNaN(value.getTime())) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);

  return value?.toString();
}

export function compareInputValues(value1: InputValue, value2: InputValue) {
  if ($ck._.isEqual(value1, value2)) return true;
  return serializeInputValue(value1)?.toLowerCase() === serializeInputValue(value2)?.toLowerCase();
}

export function isEmptyInputValue(value: InputValue | InputValue[]): value is undefined | null {
  return value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
}

export function normalizedItemListState<T extends InputSingleValue>(currentState: ListItemState<T>[]): ListItem<T>[] {
  const result = isLastItemStateEmpty(currentState) ? currentState.slice(0, -1) : currentState;

  return result.map(({ errors, dirty, ...item }) => item);
}

export function updateItemListState<T extends InputSingleValue>(
  newModelValue: ListItem<T>[],
  currentState: ListItemState<T>[],
): ListItemState<T>[] {
  const normalizedValue = normalizedItemListState(currentState);
  const hasChanges = !$ck._.isEqual(newModelValue, normalizedValue);
  // This is very important to stop an infinite loop
  if (!hasChanges) return currentState;

  return newModelValue.map((item) => {
    const existingItem = currentState.find((stateItem) => item.id === stateItem.id);
    if (!existingItem || existingItem.value !== item.value)
      return { id: $ck._.uniqueId(), ...item, errors: [], dirty: false };

    return existingItem;
  });
}

export function isLastItemStateEmpty<T extends InputSingleValue>(currentState: ListItemState<T>[]) {
  const lastItem = currentState[currentState.length - 1];
  return lastItem && !lastItem.value.trim().length;
}

export function validateListItemState<T extends InputSingleValue>(
  item: ListItemState<T>,
  {
    emptyTextError,
    isLastItem,
  }: {
    emptyTextError?: string;
    isLastItem?: boolean;
  },
) {
  return {
    ...item,
    // only validate for empty text if it's not the last item
    errors: !isLastItem && !item.value?.trim()?.length ? [emptyTextError ?? "errors.ruleIsRequiredAndNotEmpty"] : [],
  };
}

export function isSelectAppearanceSupported() {
  return CSS.supports("appearance: base-select");
}

export function mergeInputErrors<T extends string | InputErrorMessage>(a?: T[], b?: T[]) {
  return $ck._.uniqBy([...(a ?? []), ...(b ?? [])], (value) =>
    typeof value === "object" ? `${value.message} ${value.triggerId}` : value,
  );
}

export function makeInputErrorMessage<T extends InputErrorMessage>(
  message: string | InputErrorMessage,
  overrides: Omit<T, "message">,
) {
  return {
    type: "error",
    ...(typeof message === "string" ? { message } : message),
    ...overrides,
  } as T;
}

export function flattenInputErrors<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TFormErrors extends FormInputErrors<any, any> = FormInputErrors,
  TResult extends InputErrorMessage = InputErrorMessage,
>(
  inputErrors: TFormErrors | undefined,
  {
    prefix = "",
    transform = $ck.utils.PASS_THROUGH,
  }: {
    prefix?: string;
    transform?: (value: TResult, field?: string) => TResult;
  } = {},
): TResult[] {
  const castAsErrorMessages = (messages: (string | TResult)[], field?: string): TResult[] => {
    const triggerId = $ck.utils.makeKey([prefix, field]);

    return messages.map((message) =>
      transform(
        makeInputErrorMessage(message, {
          triggerId,
        } as TResult),
        field,
      ),
    );
  };

  const inputFormErrors = castAsErrorMessages(inputErrors?.formErrors ?? []);
  const inputFieldErrors = Object.entries(inputErrors?.fieldErrors ?? {}).flatMap(([field, errors]) =>
    castAsErrorMessages(errors, field),
  );

  return mergeInputErrors(inputFormErrors, inputFieldErrors);
}
